import { useEffect, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import _isEmpty from 'lodash/isEmpty'

import { MobileSortBy, Table, TableLoader } from 'components/Table'
import { SoftwareVersionCell } from '../HomeSoftwareUpdates/components/SoftwareVersionCell'

import type { HomeSoftwareHistoryType } from 'features/home/types'
import HomeSoftwareHistoryTableMobile from './HomeSoftwareHistoryTableMobile'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  homeSoftwareHistoryResponseSelector,
  homeSoftwareHistorySelector,
  selectedHomeSelector,
} from 'features/home/selectors'
import { SOFTWARE_HISTORY_HEADERS } from './const'
import { getSortOptions } from 'components/Table/utils'
import {
  HomeSoftwareEmptyBlock,
  HomeSoftwareUpdateTableContainer,
} from '../styles'
import SomethingWentWrongBlock from 'components/SomethingWentWrongBlock'
import { TableWithPaginationWrapper } from 'components/Table/styles'
import {
  fetchHomeSoftwareHistoryThunk,
  homeSoftwareHistoryActions,
} from '../slice'

const VersionCell = (cell: Cell<HomeSoftwareHistoryType>) => {
  return cell.value && <SoftwareVersionCell version={cell.value} />
}

const DateCell = (cell: Cell) => (
  <IntlFormattedDate date={cell.value} withTime defaultReturn="-" />
)

//todo localization
function HomeSoftwareHistory() {
  const dispatch = useAppDispatch()
  const selectedHome = useAppSelector(selectedHomeSelector.data)
  const softwareHistory = useAppSelector(homeSoftwareHistorySelector)
  const isSoftwareHistoryFetching = useAppSelector(
    homeSoftwareHistoryResponseSelector.isFetching
  )
  const isSoftwareHistoryLoaded = useAppSelector(
    homeSoftwareHistoryResponseSelector.isFinished
  )
  const hasSoftwareHistoryError = useAppSelector(
    homeSoftwareHistoryResponseSelector.error
  )

  useEffect(() => {
    if (selectedHome) {
      dispatch(fetchHomeSoftwareHistoryThunk(selectedHome.id))
    }

    return () => {
      dispatch(homeSoftwareHistoryActions.clear())
    }
  }, [dispatch, selectedHome])

  const headers = useMemo(
    () => ({
      runtimeVersion: {
        label: 'Runtime Version',
        id: SOFTWARE_HISTORY_HEADERS.RUNTIME_VERSION,
      },
      installed: {
        label: 'Installed',
        id: SOFTWARE_HISTORY_HEADERS.INSTALLED,
      },
    }),
    []
  )

  const columns: readonly Column<HomeSoftwareHistoryType>[] = useMemo(
    () => [
      {
        id: headers.runtimeVersion.id,
        Header: headers.runtimeVersion.label,
        accessor: (prop) => prop.version.name,
        Cell: VersionCell,
      },
      {
        id: headers.installed.id,
        Header: headers.installed.label,
        accessor: (row) => row.created,
        Cell: DateCell,
      },
    ],
    [headers]
  )
  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])
  const defaultSortBy = sortOptions[1]
  const [sortBy, setSortBy] =
    useState<SortingRule<HomeSoftwareHistoryType>>(defaultSortBy)

  return (
    <TableWithPaginationWrapper>
      <MobileSortBy<HomeSoftwareHistoryType>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {isSoftwareHistoryFetching && <TableLoader />}

      {hasSoftwareHistoryError && <SomethingWentWrongBlock />}

      {!isSoftwareHistoryFetching && isSoftwareHistoryLoaded && (
        <>
          {_isEmpty(softwareHistory) ? (
            <HomeSoftwareEmptyBlock>
              No history available
            </HomeSoftwareEmptyBlock>
          ) : (
            <HomeSoftwareUpdateTableContainer>
              <Table
                columns={columns}
                data={softwareHistory}
                defaultSortBy={defaultSortBy}
                sortBy={sortBy}
                setSortBy={setSortBy}
                mobileViewComponent={HomeSoftwareHistoryTableMobile}
              />
            </HomeSoftwareUpdateTableContainer>
          )}
        </>
      )}
    </TableWithPaginationWrapper>
  )
}

export default HomeSoftwareHistory
